import { useState } from "react";
import { FiBellOff, FiBell } from "react-icons/fi";
import useAuthStore from "../../store/authStore";
import useChatStore from "../../store/chatStore";
import {
  getConversationName, getConversationAvatar, getInitials,
  formatConversationTime, isConversationOnline,
} from "../../utils/helpers";
import styles from "./ConversationItem.module.css";

const ConversationItem = ({ conversation }) => {
  const { user } = useAuthStore();
  const {
    activeConversation, setActiveConversation, onlineUsers,
    unreadCounts, mutedConversations, muteConversation, getTypingInConversation,
  } = useChatStore();
  const [showMenu, setShowMenu] = useState(false);

  const isActive  = activeConversation?._id === conversation._id;
  const name      = getConversationName(conversation, user._id);
  const avatar    = getConversationAvatar(conversation, user._id);
  const online    = isConversationOnline(conversation, user._id, onlineUsers);
  const lastMsg   = conversation.lastMessage;
  const unread    = !isActive ? (unreadCounts[conversation._id] || 0) : 0;
  const isMuted   = (mutedConversations || []).includes(conversation._id);
  const typing    = getTypingInConversation(conversation._id);
  const isTyping  = typing.length > 0 && !isActive;

  const handleMute = (e) => {
    e.stopPropagation();
    muteConversation(conversation._id);
    setShowMenu(false);
  };

  const getLastMsgPreview = () => {
    if (!lastMsg) return null;
    const prefix = lastMsg.sender?._id === user._id ? "You: " : "";
    if (lastMsg.isVoice) return prefix + "🎤 Voice message";
    if (lastMsg.gifUrl)  return prefix + "GIF";
    if (lastMsg.file)    return prefix + "📎 " + (lastMsg.fileName || "File");
    if (lastMsg.image && !lastMsg.text) return prefix + "📷 Photo";
    return prefix + (lastMsg.text || "");
  };

  return (
    <div
      className={`${styles.item} ${isActive ? styles.active : ""} ${unread > 0 && !isActive && !isMuted ? styles.hasUnread : ""}`}
      onClick={() => { setActiveConversation(conversation); setShowMenu(false); }}
      onContextMenu={(e) => { e.preventDefault(); setShowMenu(!showMenu); }}
    >
      <div className="avatar-wrapper" style={{ position: "relative" }}>
        <div className="avatar" style={{ width: 44, height: 44, fontSize: 15 }}>
          {avatar ? <img src={avatar} alt={name} /> : getInitials(name)}
        </div>
        {online && !isMuted && <span className="online-dot" />}
        {/* Unread count bubble on avatar — like WhatsApp */}
        {unread > 0 && !isActive && !isMuted && (
          <span className={styles.avatarBadge}>{unread > 99 ? "99+" : unread}</span>
        )}
        {unread > 0 && !isActive && isMuted && (
          <span className={`${styles.avatarBadge} ${styles.avatarBadgeMuted}`}>
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </div>

      <div className={styles.info}>
        <div className={styles.top}>
          <div className={styles.nameRow}>
            <span className={`${styles.name} ${unread > 0 && !isActive && !isMuted ? styles.nameUnread : ""}`}>
              {name}
            </span>
            {isMuted && <FiBellOff size={11} className={styles.muteIcon} />}
            {conversation.isGroup && <span className={styles.groupTag}>Group</span>}
          </div>
          <div className={styles.topRight}>
            <span className={`${styles.time} ${unread > 0 && !isActive && !isMuted ? styles.timeUnread : ""}`}>
              {lastMsg ? formatConversationTime(conversation.lastMessageAt) : ""}
            </span>
            {/* WhatsApp-style unread badge */}
            {unread > 0 && !isActive && !isMuted && (
              <span className={styles.unreadBadge}>{unread > 99 ? "99+" : unread}</span>
            )}
            {/* Muted but has messages — show grey count */}
            {unread > 0 && !isActive && isMuted && (
              <span className={styles.mutedBadge}>{unread > 99 ? "99+" : unread}</span>
            )}
          </div>
        </div>
        <div className={styles.preview}>
          {isTyping ? (
            <span className={styles.typingPreview}>
              <span className={styles.typingDot} /><span className={styles.typingDot} /><span className={styles.typingDot} />
              <span>{typing[0].userName} is typing…</span>
            </span>
          ) : (
            <span className={`${styles.lastMsg} ${unread > 0 && !isActive && !isMuted ? styles.lastMsgUnread : ""}`}>
              {getLastMsgPreview() || <span className={styles.noMsg}>Start a conversation</span>}
            </span>
          )}
        </div>
      </div>

      {/* Right-click context menu for mute */}
      {showMenu && (
        <div className={styles.contextMenu} onClick={(e) => e.stopPropagation()}>
          <button className={styles.contextItem} onClick={handleMute}>
            {isMuted ? <><FiBell size={13} /> Unmute</> : <><FiBellOff size={13} /> Mute</>}
          </button>
        </div>
      )}
    </div>
  );
};

export default ConversationItem;
